/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	HostListener,
	Inject,
	Input,
	OnInit,
	PLATFORM_ID
} from '@angular/core';
import { DeviceService, TaggingService, WINDOW } from '@shared/data-access/services';

import { SeoService } from '@seo/data-access';
import { Subscription } from 'rxjs';

import { take, tap } from 'rxjs/operators';
import { BreakPoint, FeatureConfigurationKeys } from '@shared/data-access/enums';
import { CategorySEOData, CategoryChildVO } from '@category/data-access';

@Component({
	selector: 'clp-category-header',
	templateUrl: './category-header.component.html',
	styleUrl: './category-header.component.scss',
	standalone: false
})
export class CategoryHeaderComponent implements OnInit {
	@Input()
	categoryData: CategorySEOData;

	@Input()
	homrContent: any;

	activeCategorySubscription: Subscription;
	isMobile = false;
	isTablet = false;
	categoriesDataStore = [];
	createTag = TaggingService.createSimpleTag;
	eagerImagePosition = 1;
	lazyImagePosition = 5;
	maxDesktopImage = 7;
	mobileImageAddition = 2;

	constructor(
		@Inject(PLATFORM_ID) protected platformId: string,
		@Inject(WINDOW) private readonly window: Window,
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly deviceService: DeviceService,
		private readonly seoService: SeoService,
		private readonly cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			// #PZ-411
			if (typeof this.window['dcsgPz']?.featuresHandler?.getFeature === 'function') {
				const featureConf = this.window['dcsgPz'].featuresHandler.getFeature(
					FeatureConfigurationKeys.ADD_DRAWER,
					// eslint-disable-next-line @typescript-eslint/no-shadow
					(featureConf) => {
						if (featureConf?.enabled) {
							this.addDrawers(featureConf.params.drawers);
						}
					}
				);
				if (featureConf?.enabled) {
					this.addDrawers(featureConf.params.drawers);
				}
			}

			// Check the breakpoint
			this.onResize();
		}
	}

	/**
	 * @description
	 * Function to sort and add drawers to categoryData.childVOs
	 *
	 * @param drawers array of drawers
	 */
	addDrawers(drawers): void {
		this.categoryData.childVOs = this.categoryData.childVOs ?? [];
		drawers
			.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
			.forEach((drawerConf) => {
				this.categoryData.childVOs.splice(drawerConf.position, 0, {
					em: drawerConf.id,
					targetImage: drawerConf.img,
					url: drawerConf.url,
					name: drawerConf.text
				} as CategoryChildVO);
			});
	}

	/**
	 * @description
	 * Function to handle the click on Category
	 *
	 * @param index
	 */
	categoryClick(event, index): void {
		this.categoryData.childVOs.forEach((category, ind) => {
			if (index !== ind) {
				category.panelOpenState = false;
			} else {
				this.getSubcategories(this.categoryData.childVOs[ind].seoUrl);
			}
		});

		this.setFlyoutTop(event);
		this.cdr.detectChanges();
	}

	/**
	 * @description
	 * Sets the top poistion for flyout
	 *
	 * @param index
	 */
	/* istanbul ignore next */
	private setFlyoutTop(event): void {
		let top = event.currentTarget.getBoundingClientRect().top;
		top -= document.querySelector('#responsive-header-page')
			? document.querySelector('#responsive-header-page').clientHeight
			: 0;
		top -= document.querySelector('#responsive-header')
			? document.querySelector('#responsive-header').clientHeight
			: 0;
		top += window.scrollY;
		top = `${top}px`;

		event.currentTarget.nextElementSibling.style.top = top;
	}

	/**
	 * @description
	 * Function to get the subcategories based on the array of seo-tokens
	 *
	 */
	getSubcategories(subCatToken): void {
		if (this.activeCategorySubscription) {
			this.activeCategorySubscription.unsubscribe();
		}

		if (this.categoriesDataStore[subCatToken]) {
			this.mapCategoryData(this.categoriesDataStore[subCatToken]);
		} else {
			this.activeCategorySubscription = this.seoService
				.getsubCatSEOData$(subCatToken, 1)
				.pipe(
					take(1),
					tap((res: any) => {
						/* istanbul ignore next */
						if (res?.categories?.[0]) {
							const subCategories = this.setCategoryData(res.categories[0]);
							this.categoriesDataStore[subCatToken] = subCategories;
							this.mapCategoryData(subCategories);
						}
					})
				)
				.subscribe();
		}
	}

	/**
	 * @description
	 * Function to set the category details like em, url, imagePath
	 *
	 */
	setCategoryData(subCategories: CategorySEOData): CategorySEOData {
		const subCat = subCategories;
		subCat.expanded = false;
		subCat.url = `/${subCat.pageType}/${subCat.seoUrl}`;
		const categoryName = subCat.note ? subCat.note : subCat.name;
		subCat.em = `LN_${categoryName}`;
		if (subCat.childVOs) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			subCat.childVOs.forEach((category, index) => {
				category.url = `/${category.pageType}/${category.seoUrl}`;
				category.fullImage = this.seoService.getimagePath(category);
				const subcategoryName = category.note ? category.note : category.name;
				category.em = `LN_${categoryName}_${subcategoryName}`;
			});
		}
		const updatedsubCat = subCat;
		updatedsubCat.childVOs = subCat.childVOs.filter((category) => category.fullImage !== '');
		return updatedsubCat;
	}

	private mapCategoryData(catData: CategorySEOData): void {
		this.categoryData.childVOs.forEach((category) => {
			if (catData && category?.catgroupId === catData.catgroupId) {
				category.childVOs = catData.childVOs;
				if (category.childVOs && category.childVOs.length > 0) {
					category.panelOpenState = true;
				} else {
					category.panelOpenState = false;
				}
			}
		});
	}

	/**
	 * @param event - Event
	 * @param index - The current category
	 *
	 * @description
	 * Function called on Category keyup
	 */
	categoryKeyUp(event, index): void {
		if (event.keyCode === 13) {
			this.categoryClick(event, index);
		}
	}

	togglebodyClass(panel): void {
		if (panel.panelOpenState) {
			// Add body class to prevent body scrolling
			this.document.body.classList.add('overflow-hidden');
		} else {
			// Remove body class to prevent
			this.document.body.classList.remove('overflow-hidden');
		}
	}
	@HostListener('window:resize', ['$event'])
	onResize(): void {
		// Check the breakpoint
		if (
			this.deviceService.getDeviceBreakPoint() === BreakPoint.EXTRASMALL ||
			this.deviceService.getDeviceBreakPoint() === BreakPoint.SMALL
		) {
			this.isMobile = true;
			this.isTablet = false;
		} else if (this.deviceService.getDeviceBreakPoint() === BreakPoint.MEDIUM) {
			this.isTablet = true;
			this.isMobile = false;
		} else {
			this.isMobile = false;
			this.isTablet = false;
		}
	}

	// Hide the opened categories on scroll
	hideCategory(event): void {
		if (event.target.scrollTop === event.target.scrollHeight - event.target.offsetHeight) {
			event.currentTarget.classList.remove('opacity');
		} else {
			event.currentTarget.classList.add('opacity');
		}
		this.hideAllSubCategories();
	}

	// Hide category on mouse out
	hideAllSubCategories(): void {
		this.categoryData.childVOs.forEach((category) => {
			category.panelOpenState = false;
		});
		this.cdr.detectChanges();
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	trackByFn(index: number, _item: any): number {
		return index;
	}
}
